/**
 * TypeScript mirror of shared/models/readiness_finding.py.
 *
 * Keep field names and closed-literal values in lockstep with the
 * Python model — the Python factory is the source of truth, this
 * file is a transport-layer mirror. Adding a new ConsumerKind /
 * Severity / Phase / Owner / PrimaryAction means editing BOTH this
 * file AND the Python model AND the diagnostic_copy lint allowlist.
 */

/** Three-tier severity. UI maps to "To fix" / "FYI". */
export type Severity = "blocker" | "advisory" | "info";

/** Which phase produced the finding. Internal — never shown to user. */
export type Phase = "pre_mapping" | "mapping" | "data_join" | "output_render";

/** Inline-resolution kinds for pre-mapping findings (mirrors ResolutionKind). */
export type ResolutionKind =
  | "set_constant"
  | "pick_source"
  | "register_lookup"
  | "add_transformation"
  | "skip_column"
  | "generate_uuid"
  | "generate_sequence"
  | "comment";

/** One inline resolution offered on a pre-mapping finding. */
export interface ResolutionOption {
  kind: ResolutionKind;
  label: string;
  payload_schema: Record<string, unknown>;
}

/** Which phase owns the *fix*. Drives agent self-correction + UI deep-link routing. */
export type Owner =
  | "mapper"
  | "source_assignment"
  | "quality"
  | "schema"
  | "assembly";

/** Single primary action shown on a finding card. */
export type PrimaryAction =
  | "fix_in_mapping"
  | "review_data"
  | "adjust_schema"
  | "none";

/** Which Readiness consumer this finding applies to. */
export type ConsumerKind =
  | "target_table"
  | "pipeline_output"
  | "hierarchical_assembly"
  | "destination"
  | "mapping_canvas";

/** Three-state readiness status, derived from a list of findings. */
export type ReadinessStatus = "ready" | "ready_with_notes" | "not_ready";

/**
 * One readiness check result. Mirrors ReadinessFinding from the Python
 * model. Field order matches the model for diffability.
 */
export interface ReadinessFinding {
  /** Stable internal code (e.g. CARDINALITY_BINDING_INVALID). Never shown to user. */
  code: string;
  severity: Severity;
  phase: Phase;
  owner: Owner;

  /** XPath-style for hierarchical, table.column for tabular. */
  target_path: string;
  target_element: string;

  /** Identity of the parent record (e.g. {SubjectKey: "S-001"}); null when schema-wide. */
  parent_identity: Record<string, unknown> | null;

  /** What the check actually observed. Check-specific keys. */
  observed: Record<string, unknown>;
  /** What the check expected. Check-specific keys. */
  expected: Record<string, unknown>;
  /** Up to N sample affected source row identifiers. */
  affected_source_rows: Array<Record<string, unknown>>;

  /** Plain-English title (≤8 words). Lint-enforced no-jargon. */
  user_title: string;
  /** Plain-English sentence with quantities and source-domain nouns. */
  user_detail: string;

  primary_action: PrimaryAction;
  /** Payload that drives the deep-link button. */
  primary_action_target: Record<string, unknown>;
  /** Consumer panels this finding should surface on. Empty = universal. */
  applicable_consumers: ConsumerKind[];

  /** Inline resolution options (populated only for pre_mapping findings). */
  resolution_options?: ResolutionOption[];
  /** Stable per-finding id used by the resolve endpoint. */
  finding_id?: string;
}

/** One operator answer submitted from a pre_mapping finding's inline form. */
export interface ReadinessResolutionRequest {
  finding_id: string;
  kind: ResolutionKind;
  payload: Record<string, unknown>;
  target_table: string;
  target_column: string;
}

/** Wire shape of POST /api/v1/mapping/readiness/{batch_id}. */
export interface ReadinessReportResponse {
  run_id: string;
  batch_id: string;
  findings: ReadinessFinding[];
  blocking_count: number;
  advisory_count: number;
  info_count: number;
  duration_ms: number;
  target_constants: Record<string, string>;
  registered_sources: string[];
}

/** Wire shape of POST /api/v1/mapping/readiness/resolve. */
export interface ReadinessResolveResponse {
  batch_id: string;
  target_constants: Record<string, string>;
  source_picks: Record<string, { source_table: string; source_column: string }>;
  transformations: Array<Record<string, unknown>>;
  skip_columns: string[];
  registered_lookups: Array<Record<string, unknown>>;
  resolved_finding_ids: string[];
}

/**
 * Response shape from POST /api/v1/agents/sessions/{id}/readiness.
 * Uniform across all consumer kinds; the ``preview`` discriminator
 * varies by kind so callers should narrow on it.
 */
export interface ReadinessResponse {
  consumer_kind: ConsumerKind;
  status: ReadinessStatus;
  findings: ReadinessFinding[];
  preview:
    | { kind: "none"; payload: null }
    | { kind: "mapping_summary"; payload: Record<string, unknown> }
    | { kind: "table_rows"; payload: Record<string, unknown> }
    | { kind: "multi_table"; payload: Record<string, unknown> }
    | { kind: "xml"; payload: { output_ref: string } }
    | { kind: "connector_summary"; payload: Record<string, unknown> };
  /** assembly:// or workspace:// reference, null when not ready or N/A. */
  submission_ref: string | null;
  /** Optional explanatory copy (e.g. "No assembly has been run yet."). */
  message?: string;
}

/**
 * Aggregation key used by the UI's root-cause grouper to collapse
 * duplicate findings into a single card with "Affects N fields".
 * Mirrors ReadinessFinding.aggregation_key() on the Python side.
 */
export function aggregationKey(f: ReadinessFinding): string {
  const srcTable = (f.observed.source_table as string) || "";
  const srcCol = (f.observed.source_column as string) || "";
  return `${f.code}|${srcTable}|${srcCol}`;
}

/**
 * Collapse a flat findings array into a unique-by-root-cause list.
 * Used by chips + banners + "Issues in this output" headers so the
 * count the user sees is consistent across surfaces. Order is
 * preserved (first occurrence wins) so the most-recent emission is
 * what the user sees first.
 *
 * Sharing one helper across the toolbar chip, AssemblyInspectorPanel
 * chip, ReviewPane banner, and HierarchicalPreview heading prevents
 * the "8 here / 2 there" inconsistency that confuses users about
 * which number is the real one.
 */
export function aggregateByRootCause(
  findings: ReadinessFinding[],
): ReadinessFinding[] {
  const seen = new Set<string>();
  const out: ReadinessFinding[] = [];
  for (const f of findings) {
    const key = aggregationKey(f);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(f);
  }
  return out;
}

/**
 * Derive a status from a flat list of findings. Mirrors
 * AssemblyResult.status() server-side. Used by the mapping-canvas
 * pill (union across consumers) and any client-side aggregator.
 */
export function deriveStatus(findings: ReadinessFinding[]): ReadinessStatus {
  if (findings.some((f) => f.severity === "blocker")) return "not_ready";
  if (findings.some((f) => f.severity === "advisory")) return "ready_with_notes";
  return "ready";
}
